import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { usePriorities } from "@/context/PrioritiesContext";
import { useClient } from "@/context/ClientContext";
import LeadsDashboard from "@/pages/LeadsDashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";
import { PriorityBadge, StatusBadge } from "@/components/Badges";
import {
  Activity, Clock, UserCheck, CheckCircle2, AlertTriangle, ArrowUpRight,
  Filter, RefreshCw,
} from "lucide-react";
import ChartSkeleton from "@/components/dashboard/ChartSkeleton";

// Each chart ships as its own webpack chunk — recharts only loads when needed.
const SLAPerformanceChart = lazy(() => import("@/components/dashboard/SLAPerformanceChart"));
const WeeklyTrendChart = lazy(() => import("@/components/dashboard/WeeklyTrendChart"));
const StatusFunnelChart = lazy(() => import("@/components/dashboard/StatusFunnelChart"));
const ChannelMixChart = lazy(() => import("@/components/dashboard/ChannelMixChart"));
const DeptLoadChart = lazy(() => import("@/components/dashboard/DeptLoadChart"));
const PriorityHeatmap = lazy(() => import("@/components/dashboard/PriorityHeatmap"));
const AgentLeaderboardChart = lazy(() => import("@/components/dashboard/AgentLeaderboardChart"));
const FRTHistogramChart = lazy(() => import("@/components/dashboard/FRTHistogramChart"));
const OpsWidgets = lazy(() => import("@/components/dashboard/OpsWidgets"));
const Customer360Widget = lazy(() => import("@/components/dashboard/Customer360Widget"));
const RecentlyViewedStrip = lazy(() => import("@/components/dashboard/RecentlyViewedStrip"));

const STAT_META = [
  { key: "open", label: "Open", icon: Activity, accent: "text-blue-600", sub: "Awaiting action" },
  { key: "in_progress", label: "In Progress", icon: Clock, accent: "text-amber-600", sub: "Being handled" },
  { key: "pending_user", label: "Pending User", icon: UserCheck, accent: "text-purple-600", sub: "Waiting on customer" },
  { key: "resolved_closed", label: "Resolved / Closed", icon: CheckCircle2, accent: "text-emerald-600", sub: "Today" },
  { key: "critical_active", label: "Critical Active", icon: AlertTriangle, accent: "text-rose-600", sub: "Needs immediate action" },
];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const DEFAULT_FILTERS = () => ({
  date_from: daysAgo(13),
  date_to: today(),
  department_id: "all",
  channel: "all",
  priority: "all",
  status: "all",
});

const STATUS_OPTIONS = ["open", "in_progress", "pending_user", "resolved", "closed", "cancelled"];

export default function Dashboard() {
  const { active } = useClient();
  // Ooredoo / Al-Meera use a lead-centric dashboard, not the ticketing one.
  if (active?.kind === "leads") return <LeadsDashboard />;

  return <TicketingDashboard />;
}

function TicketingDashboard() {
  const { priorityOptions } = usePriorities();
  const PRIORITY_OPTIONS = priorityOptions.map((p) => p.value);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [slaRisk, setSlaRisk] = useState([]);
  const [charts, setCharts] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS());
  const [depts, setDepts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    if (p.date_from) p.date_from = `${p.date_from}T00:00:00+00:00`;
    if (p.date_to) p.date_to = `${p.date_to}T23:59:59+00:00`;
    return p;
  }, [filters]);

  const loadCharts = () => {
    setLoading(true);
    api.get("/dashboard/charts", { params })
      .then((r) => setCharts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/tickets", { params: { limit: 6 } }),
      api.get("/dashboard/sla-at-risk"),
      api.get("/departments"),
      api.get("/channels"),
    ]).then(([s, t, r, d, c]) => {
      setStats(s.data);
      setRecent(t.data);
      setSlaRisk(r.data);
      setDepts(d.data.filter((x) => x.active));
      // Keep ALL channels (incl. inactive) so the donut can colour historical
      // slices and the filter dropdown still works for retired channels.
      setChannels(c.data);
    });
  }, []);

  useEffect(() => { loadCharts(); /* eslint-disable-next-line */ }, [params]);

  const fmtTime = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Sticky filter bar */}
      <div
        className="sticky top-16 z-20 -mx-8 px-8 py-3 bg-slate-50/95 backdrop-blur border-b border-slate-200"
        data-testid="dashboard-filter-bar"
      >
        <div className="flex flex-wrap items-end gap-2.5">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1 w-36 h-9 text-xs" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1 w-36 h-9 text-xs" data-testid="filter-date-to" />
          </div>
          <FilterSelect label="Department" value={filters.department_id} onChange={(v) => setFilters({ ...filters, department_id: v })} options={[{ value: "all", label: "All" }, ...depts.map((d) => ({ value: d.id, label: d.name }))]} testid="filter-dept" />
          <FilterSelect label="Channel" value={filters.channel} onChange={(v) => setFilters({ ...filters, channel: v })} options={[{ value: "all", label: "All" }, ...channels.map((c) => ({ value: c.name, label: c.name }))]} testid="filter-channel" />
          <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[{ value: "all", label: "All" }, ...STATUS_OPTIONS.map((s) => ({ value: s, label: s.replace("_", " ") }))]} testid="filter-status" />
          <FilterSelect label="Priority" value={filters.priority} onChange={(v) => setFilters({ ...filters, priority: v })} options={[{ value: "all", label: "All" }, ...PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }))]} testid="filter-priority" />
          <Button variant="ghost" size="sm" onClick={() => setFilters(DEFAULT_FILTERS())} className="text-slate-500 h-9 ml-auto" data-testid="filter-reset">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
        </div>
      </div>

      {/* Filter KPIs */}
      {charts?.kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="In window" value={charts.kpis.total_in_window} accent="text-indigo-600" icon={Filter} />
          <Kpi label="Resolved" value={charts.kpis.resolved_in_window} accent="text-emerald-600" icon={CheckCircle2} />
          <Kpi label="Active" value={charts.kpis.active_in_window} accent="text-amber-600" icon={Clock} />
          <Kpi label="SLA breaches" value={charts.kpis.breached_in_window} accent="text-rose-600" icon={AlertTriangle} />
        </div>
      )}

      {/* Customer 360 — search-anywhere launchpad */}
      <Suspense fallback={<div className="h-24" />}>
        <Customer360Widget />
      </Suspense>

      {/* Recently viewed customers strip */}
      <Suspense fallback={<div className="h-16" />}>
        <RecentlyViewedStrip />
      </Suspense>

      {/* Legacy stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STAT_META.map((m) => {
          const v = stats?.counts?.[m.key] ?? 0;
          const Icon = m.icon;
          return (
            <Card key={m.key} data-testid={`stat-card-${m.key}`} className="lift border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{m.label}</div>
                  <Icon className={`w-4 h-4 ${m.accent}`} />
                </div>
                <div className={`font-heading text-3xl font-bold mt-3 ${m.accent}`}>{v}</div>
                <div className="text-xs text-slate-500 mt-1">{m.sub}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* === 8 CHARTS (each in its own lazy-loaded chunk) === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="charts-grid">
        <Suspense fallback={<ChartSkeleton title="SLA performance over time" />}>
          <SLAPerformanceChart data={charts?.sla_performance} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="Weekly trend" />}>
          <WeeklyTrendChart data={charts?.weekly_trend} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="Status funnel" />}>
          <StatusFunnelChart data={charts?.status_funnel} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="Channel mix" />}>
          <ChannelMixChart data={charts?.channel_mix} channels={channels} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="Department load" />}>
          <DeptLoadChart data={charts?.dept_load} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="Priority × Status heatmap" />}>
          <PriorityHeatmap data={charts?.priority_heatmap} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="Agent leaderboard" />}>
          <AgentLeaderboardChart data={charts?.agent_leaderboard} loading={loading} />
        </Suspense>
        <Suspense fallback={<ChartSkeleton title="First Response Time histogram" />}>
          <FRTHistogramChart data={charts?.frt_histogram} loading={loading} />
        </Suspense>
      </div>

      {/* === Phase 5.1 — Operational widgets (real-time, 60s refresh) === */}
      <div className="border-t border-slate-200 pt-6">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="font-heading text-base font-semibold text-slate-900">Operational pulse</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">Live · refreshes every 60s</span>
        </div>
        <Suspense fallback={<div className="h-32" />}>
          <OpsWidgets />
        </Suspense>
      </div>

      {/* Recent + SLA at risk */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="font-heading text-base font-semibold">Recent Tickets</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/tickets" className="text-indigo-600">View all <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></Link>
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                  <TableHead className="w-24">ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((t) => (
                  <TableRow key={t.id} className="hover:bg-slate-50">
                    <TableCell>
                      <Link to={`/tickets/${t.id}`} className="text-indigo-600 font-medium font-mono text-xs">{t.code}</Link>
                    </TableCell>
                    <TableCell className="text-sm">{t.customer_name}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">
                      {t.category}{t.subcategory ? ` → ${t.subcategory}` : ""}
                    </TableCell>
                    <TableCell><PriorityBadge value={t.priority} /></TableCell>
                    <TableCell><StatusBadge value={t.status} /></TableCell>
                    <TableCell className="text-right text-xs text-slate-500">{fmtTime(t.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-500" /> SLA At-Risk
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="text-xs uppercase tracking-wider text-slate-500">
                  <TableHead>ID</TableHead><TableHead>Customer</TableHead><TableHead>Pri</TableHead><TableHead>TAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slaRisk.map((t) => (
                  <TableRow key={t.id} className="hover:bg-slate-50">
                    <TableCell><Link to={`/tickets/${t.id}`} className="text-indigo-600 font-mono text-xs">{t.code}</Link></TableCell>
                    <TableCell className="text-sm truncate max-w-[120px]">{t.customer_name}</TableCell>
                    <TableCell><PriorityBadge value={t.priority} /></TableCell>
                    <TableCell className="text-xs text-rose-600 font-semibold">{t.tat}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-9 w-40 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <Card className="border-slate-200 lift">
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
          <div className={`font-heading text-2xl font-bold mt-1 ${accent}`}>{value}</div>
        </div>
        <Icon className={`w-5 h-5 ${accent}`} />
      </CardContent>
    </Card>
  );
}
